import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, EmptyState, Loader } from "@/components/common";
import { TextField, SelectField } from "@/components/fields";
import { ROLE_LABELS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users as UsersIcon, Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { name: "", email: "", phone: "", password: "", role: "staff", company_id: "" };

export default function Users() {
  const { user } = useAuth();
  const isSuper = user.role === "super_admin";
  const [items, setItems] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const roleOptions = isSuper
    ? ["property_manager", "property_admin", "accountant", "staff"]
    : ["property_admin", "accountant", "staff"];

  const load = () => {
    api.get("/users").then((r) => setItems(r.data));
    if (isSuper) api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setForm({ ...empty, role: roleOptions[0] }); setEditId(null); setOpen(true); };
  const openEdit = (u) => { setForm({ ...empty, ...u, password: "" }); setEditId(u.id); setOpen(true); };

  const save = async () => {
    if (!form.name || !form.email) return toast.error("Name and email are required");
    if (!editId && !form.password) return toast.error("Password is required");
    try {
      if (editId) {
        await api.put(`/users/${editId}`, { name: form.name, phone: form.phone, role: form.role, ...(form.password ? { password: form.password } : {}) });
      } else {
        await api.post("/users", { ...form, company_id: isSuper ? (form.role === "property_manager" ? (form.company_id || null) : form.company_id) : undefined });
      }
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!items) return <Loader />;

  return (
    <div>
      <PageHeader title={isSuper ? "Property Managers & Users" : "Staff & Users"}
        subtitle={isSuper ? "Create property managers and assign companies" : "Create admins, accountants and staff"} icon={UsersIcon}
        actions={<Button onClick={openNew} data-testid="add-user-btn" className="gap-2"><Plus size={16} /> Add User</Button>} />

      {items.length === 0 ? (
        <EmptyState icon={UsersIcon} title="No users yet" message="Create your first team member."
          action={<Button onClick={openNew}><Plus size={16} className="mr-2" />Add User</Button>} />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead>
                <TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-sm">{u.email}</TableCell>
                    <TableCell className="text-sm">{u.phone || "—"}</TableCell>
                    <TableCell><Badge variant="outline">{ROLE_LABELS[u.role]}</Badge></TableCell>
                    <TableCell><StatusBadge status={u.active ? "active" : "disabled"} /></TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(u)}><PencilSimple size={16} /></Button>
                        <Button size="icon" variant="ghost" onClick={() => del(u.id)}><Trash size={16} className="text-destructive" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editId ? "Edit User" : "Add User"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <TextField label="Name" value={form.name} onChange={(v) => set("name", v)} required testId="user-name" />
            <TextField label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} required testId="user-email" />
            <TextField label="Phone" value={form.phone} onChange={(v) => set("phone", v)} />
            <TextField label={editId ? "New Password (optional)" : "Password"} type="password" value={form.password} onChange={(v) => set("password", v)} testId="user-password" />
            <SelectField label="Role" value={form.role} onChange={(v) => set("role", v)}
              options={roleOptions.map((r) => ({ value: r, label: ROLE_LABELS[r] }))} testId="user-role" />
            {isSuper && form.role !== "property_manager" && (
              <SelectField label="Company" value={form.company_id} onChange={(v) => set("company_id", v)}
                options={companies.map((c) => ({ value: c.id, label: c.name }))} />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-user-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
